"use client";
import React, { useState, useEffect } from 'react';

const CARDS = [
  { id: 1, type: 'gpu', icon: '🎮' },
  { id: 2, type: 'vr', icon: '🥽' },
  { id: 3, type: 'phone', icon: '📱' },
  { id: 4, type: 'laptop', icon: '💻' },
  { id: 5, type: 'watch', icon: '⌚' },
  { id: 6, type: 'mouse', icon: '🖱️' },
];

const generateDeck = () => {
  const deck = [...CARDS, ...CARDS].map(card => ({
    ...card,
    uniqueId: Math.random().toString(36).substr(2, 9),
    isFlipped: false,
    isMatched: false,
  }));
  return deck.sort(() => Math.random() - 0.5);
};

const GadgetMatch = () => {
  const [cards, setCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [moves, setMoves] = useState(0);
  const [matches, setMatches] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [gameOver, setGameOver] = useState(false);

  useEffect(() => {
    setCards(generateDeck());
  }, []);

  const handleCardClick = (uniqueId) => {
    if (isLocked) return;
    const clickedCard = cards.find(c => c.uniqueId === uniqueId);
    if (clickedCard.isFlipped || clickedCard.isMatched) return;

    const newCards = cards.map(c => 
      c.uniqueId === uniqueId ? { ...c, isFlipped: true } : c
    );
    setCards(newCards);

    const newFlipped = [...flippedCards, clickedCard];
    setFlippedCards(newFlipped);

    if (newFlipped.length === 2) {
      setIsLocked(true);
      setMoves(m => m + 1);

      if (newFlipped[0].type === newFlipped[1].type) {
        // Match found
        setTimeout(() => {
          setCards(prev => prev.map(c => 
            c.type === newFlipped[0].type ? { ...c, isMatched: true } : c
          ));
          setFlippedCards([]);
          setIsLocked(false);
          setMatches(m => {
            const newMatches = m + 1;
            if (newMatches === CARDS.length) setGameOver(true);
            return newMatches;
          });
        }, 500);
      } else {
        // No match
        setTimeout(() => {
          setCards(prev => prev.map(c => 
            (c.uniqueId === newFlipped[0].uniqueId || c.uniqueId === newFlipped[1].uniqueId) 
              ? { ...c, isFlipped: false } : c
          ));
          setFlippedCards([]);
          setIsLocked(false);
        }, 1000);
      }
    }
  };

  const resetGame = () => {
    setCards(generateDeck());
    setFlippedCards([]);
    setMoves(0);
    setMatches(0);
    setGameOver(false);
    setIsLocked(false);
  };

  return (
    <div className="w-full bg-brand-surface border border-gray-300 p-6 rounded-xl shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold text-brand-text tracking-tight">Gadget Match</h2>
          <p className="text-xs text-brand-muted uppercase tracking-widest mt-1">Memory Test</p>
        </div>
        <div className="text-right">
          <div className="text-sm font-bold text-brand-muted">Moves: <span className="text-brand-text">{moves}</span></div>
          <div className="text-xs text-brand-muted">Pairs: {matches}/{CARDS.length}</div>
        </div>
      </div>

      {!gameOver ? (
        <div className="grid grid-cols-4 gap-3 md:gap-4 mb-4">
          {cards.map(card => (
            <div 
              key={card.uniqueId}
              onClick={() => handleCardClick(card.uniqueId)}
              className={`aspect-square cursor-pointer transition-all duration-300 transform rounded-lg flex items-center justify-center text-3xl shadow-sm border border-gray-300
                ${(card.isFlipped || card.isMatched) ? 'bg-brand-surface-alt rotate-0' : 'bg-gray-800 hover:bg-[var(--brand-surface-hover)] rotate-y-180'}
                ${card.isMatched ? 'opacity-50 ring-2 ring-gray-400' : ''}`}
              style={{ transformStyle: 'preserve-3d' }}
            >
              <div className={`transition-opacity duration-300 ${(card.isFlipped || card.isMatched) ? 'opacity-100' : 'opacity-0'}`}>
                {card.icon}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="py-12 text-center">
          <div className="text-5xl mb-4">🏆</div>
          <h3 className="text-2xl font-bold text-brand-text mb-2">Memory Optimized!</h3>
          <p className="text-brand-muted mb-6">You completed the diagnostic in {moves} moves.</p>
          <button 
            onClick={resetGame}
            className="px-6 py-2 bg-brand-secondary text-brand-text-inverse font-bold rounded-full hover:bg-[var(--brand-surface-hover)] transition-all shadow-sm"
          >
            Restart Scan
          </button>
        </div>
      )}
    </div>
  );
};

export default GadgetMatch;
